/**
 * Normalize process-parameter payloads before POST/PUT (JSON-safe strings).
 *
 * Times go out as bare HH:mm. Hive anchors them onto the production day, moving
 * a phase that reads earlier than the one before it to the next day, so a batch
 * that bakes past midnight keeps a duration that makes sense.
 */

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Extract wall-clock HH:mm — never apply timezone shift. */
export function extractClockTime(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value).trim();
  if (!str) return null;

  const fromIso = str.match(/T(\d{1,2}):(\d{2})/);
  if (fromIso) {
    return `${fromIso[1].padStart(2, '0')}:${fromIso[2]}`;
  }

  const hmOnly = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hmOnly) {
    return `${hmOnly[1].padStart(2, '0')}:${hmOnly[2]}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${pad2(value.getUTCHours())}:${pad2(value.getUTCMinutes())}`;
  }

  const d = new Date(str);
  if (!Number.isNaN(d.getTime()) && str.endsWith('Z')) {
    return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  }

  return null;
}

/** HH:mm for tablet inputs from the stored phase timestamp. */
export function processParamTimeToInput(value: unknown): string {
  return extractClockTime(value) || '';
}

/**
 * HH:mm for display anywhere a scheduled time is shown.
 *
 * Schedule columns (MixingStart, BakingEnd…) are timezone-naive in Postgres, so
 * Prisma serializes the stored wall clock with a `Z` suffix it does not have.
 * `new Date(...).toLocaleTimeString()` believes that suffix and shifts the times
 * by the local offset — the 04:00 shift start used to render as 06:00 in CEST.
 * Reading the literal digits keeps every view agreeing with the Gantt.
 */
export function formatClockTime(value: unknown, fallback = '-'): string {
  return extractClockTime(value) ?? fallback;
}

/** Minutes from midnight for Gantt axes (wall-clock, no timezone shift). */
export function clockTimeToMinutes(timeStr?: string): number | null {
  if (!timeStr) return null;
  const clock = extractClockTime(timeStr);
  if (!clock) return null;
  const [h, m] = clock.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

const TIME_FIELDS = [
  'MixingStart',
  'MixingEnd',
  'BulkFermentationStart',
  'BulkFermentationEnd',
  'FinalProofStart',
  'FinalProofEnd',
  'BakingStart',
  'BakingEnd',
  'CoolingStart',
  'PackagingStart',
  'PackagingEnd',
] as const;

function toDateOnly(value: unknown): string | undefined {
  return extractCalendarDate(value) ?? undefined;
}

/**
 * Wall-clock yyyy-mm-dd from a stored naive timestamp.
 *
 * Same rule as `extractClockTime`: Prisma stamps timezone-naive columns with a
 * `Z` they do not have, so `new Date(...).getDate()` can slip across midnight.
 * The ISO date digits are the calendar day the bakery actually scheduled.
 */
export function extractCalendarDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().split('T')[0];
  }
  const str = String(value).trim();
  if (!str) return null;
  const dateOnly = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) return dateOnly[1];
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split('T')[0];
}

function normalizeTimeForApi(value: unknown): string | null {
  return extractClockTime(value);
}

export function serializeProcessParameterForApi(
  data: Record<string, unknown>
): Record<string, unknown> {
  const productionDate = toDateOnly(data.ProductionDate);
  const out: Record<string, unknown> = { ...data };

  if (productionDate) {
    out.ProductionDate = productionDate;
  }

  for (const key of TIME_FIELDS) {
    if (key in out) {
      out[key] = normalizeTimeForApi(out[key]);
    }
  }

  return out;
}
